import { captureMessage } from '@sentry/nextjs';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { isEmpty } from '@/utils/common';

const ObjectUTMToSend = {
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_term: '',
  retain_url: '',
  redirect_uri: '',
  zoneclick: '',
  nameelement: '',
};

const getUtmFromUrl = (href: string) => {
  if (!href) return '';
  const regexCheckUtm = new RegExp(/utm_/);
  const hrefWithUtm = regexCheckUtm.test(href) ? href : null;
  if (!hrefWithUtm) return '';
  const regexGetUtm = new RegExp(/utm_(.*)(?=\#)/);
  const utmWithHash = regexGetUtm.exec(hrefWithUtm + '#');
  if (!utmWithHash || !utmWithHash?.[0]) return '';
  const regexGetHash = new RegExp(/\#(.*)/);
  const utm = utmWithHash[0].replace(regexGetHash, '');
  return utm;
};

const getOriginalUTM = () => {
  const originalUTM = isEmpty(getUtmFromUrl(window.location.href))
    ? window.localStorage.getItem('originalUTM')
    : getUtmFromUrl(window.location.href);

  let decodedOriginalUTM = '-';

  if (originalUTM) {
    try {
      decodedOriginalUTM = decodeURI(originalUTM);
    } catch (e) {
      decodedOriginalUTM = originalUTM;
      captureMessage(`failed to decode UTM, ${originalUTM}`, { level: 'warning' });
    }
  }

  return decodedOriginalUTM;
};

export const useFormatConsoleUrl = (
  url: string,
  zoneclick: 'body' | 'header' | 'footer' | 'banner',
  elementText: string,
  redirectUri?: string,
) => {
  const [formattedURL, setFormattedURL] = useState<string>('');

  const useAsPath = (): string => {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    return useMemo(() => {
      const queryString = searchParams && !isEmpty(Array.from(searchParams)) ? `?${searchParams.toString()}` : '';

      return pathname + queryString;
    }, [pathname, searchParams]);
  };

  const asPath = useAsPath();

  useEffect(() => {
    if (!window) return;
    const decodedOriginalUTM = getOriginalUTM();

    const objectUTM: Partial<Record<string, string>> = {};

    decodedOriginalUTM?.split('&')?.forEach(item => {
      const utmArr = item.split('=');
      objectUTM[utmArr[0]] = utmArr[1];
    });

    objectUTM.zoneclick = zoneclick;
    objectUTM.nameelement = elementText;
    objectUTM.retain_url = location.toString().replace(location.search, '');

    if (redirectUri) objectUTM.redirect_uri = redirectUri;

    let linkUTM = '';

    Object.keys(objectUTM).forEach(key => {
      if (key in ObjectUTMToSend) {
        linkUTM += `&${key}=${objectUTM[key]}`;
      }
    });

    setFormattedURL(`${url}${linkUTM ? `?${linkUTM.slice(1)}` : ''}`);
  }, [elementText, url, zoneclick, asPath, redirectUri]);

  return formattedURL;
};
